import React, { useState } from 'react';
import { connect } from 'react-redux';

import { Button } from 'components/ui/button';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';
import PropertyMergeStep from 'components/properties/merge/property_merge_step';
import { requiredFields } from 'components/properties/merge/helpers';
import { postMerge } from 'components/properties/merge/api';

import { hideMergeRecordsModal } from '../../store/actions/properties';
import { Errors } from 'components/ui/errors';

type MergePropertiesModalProps = {
  selectedPropertyIds: number[];
  shown: boolean;
  onClose: () => void;
}

export function MergePropertiesModal({ shown, selectedPropertyIds, onClose }: MergePropertiesModalProps) {
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [mappings, setMappings] = useState<Record<string, number | null>>({});

  const handleClose = () => {
    setConfirming(false);
    setSaving(false);
    setErrors([]);
    setMappings({});
    onClose();
  };

  const handleAdvance = (
    properties: Record<string, any>[],
    attributesMap: Record<string, number | null>,
    shownKeys: string[]
  ) => {
    const merged: Record<string, number | null> = {};
    shownKeys.forEach((key) => {
      merged[key] = attributesMap[key];
    });
    // Required fields with no conflict have no row to select; default them to the first property.
    requiredFields.forEach((key) => {
      if (!shownKeys.includes(key)) {
        merged[key] = properties[0].id;
      }
    });

    setMappings(merged);
    setConfirming(true);
  };

  const handleSubmit = async () => {
    setSaving(true);

    try {
      const response = await postMerge(selectedPropertyIds, mappings);
      window.location.href = response.nextUrl;
    } catch (error) {
      setErrors(Array.isArray(error) ? error : ['Unable to merge properties']);
      setSaving(false);
    }
  };

  return (
    <Modal open={shown} onClose={handleClose}>
      <Modal.Content size={confirming ? undefined : 'xl'}>
        <Modal.Header>
          Merge Properties
        </Modal.Header>
        {confirming ? <>
          <Modal.Body className="d-flex flex-column gap-3">
            <Errors errors={errors} title="Unable to merge properties" />
            <div>You are about to merge {selectedPropertyIds.length} properties.</div>
            <div>This action is permanent and cannot be undone. Are you sure you want to merge?</div>
          </Modal.Body>
          <Modal.Footer>
            <Button label="Cancel" variant="ghost" onClick={onClose} />
            <Button disabled={saving} label="Merge Properties" onClick={handleSubmit} />
          </Modal.Footer>
        </> : (
          <PropertyMergeStep
            propertyIds={selectedPropertyIds}
            onClose={onClose}
            onMerge={handleAdvance}
          />
        )}
      </Modal.Content>
    </Modal>
  );
}

const mapStateToProps = (state: Record<string, any>) => ({
  selectedPropertyIds: state.selectList.selectedPropertyIds,
  shown: !!state.properties.showMergeProperties,
});

const mapDispatchToProps = {
  onClose: hideMergeRecordsModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(MergePropertiesModal);
